import React, { useMemo, useCallback, useContext } from 'react';
import { PureTranslator } from 'ggApp/modules/translator';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import { ItemCard } from 'ggApp/shared/components/Cart/ItemCard';

import { TinyParagraph, SmallHeadline, Headline } from 'ggApp/shared/common/Typography';
import PlusIcon from 'ggApp/shared/icons/Quantity/Plus';
import { registerMixLpClick } from 'ggApp/modules/gaAnalytics/actions';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import {
    Container,
    ContainerHeader,
    Header,
    HeaderLink,
    HeaderContent,
    HeaderList,
    HeaderPrice,
    ContainerBody,
    AddProduct,
    HeaderTiny,
    ProductCard,
} from './styles';

export interface MixOverlayProps {
    isMixCart?: boolean;
    items: any;
    closeOverlay?: () => void;
    shouldShowLink?: boolean;
    isConfirmation?: boolean;
    isOrderReview?: boolean;
}

export const MixItemsContainer = ({
    items,
    closeOverlay,
    shouldShowLink = true,
    isConfirmation = false,
    isOrderReview = false,
}: MixOverlayProps): JSX.Element | null => {
    const EmptyCardCount = Math.max(3 - items.length, 0);
    const itemsArray = items.concat(new Array(EmptyCardCount).fill(0));
    const { cart = {}, user = {} } = useContext(OrderContext);
    const { orderNumber } = cart;
    const { id: userId } = user;

    const renderItems = useMemo(
        () =>
            itemsArray.map((item: any, key: number) => {
                let card;
                let itemNum = 'FIRST';
                if (key === 1) {
                    itemNum = 'SECOND';
                } else if (key === 2) {
                    itemNum = 'THIRD';
                }

                if (Object.keys(item).length > 0) {
                    card = isConfirmation ? (
                        <ProductCard item={item} shouldShowPrice={false} />
                    ) : (
                        <ItemCard item={item} cartType="MIX" isOrderReview={isOrderReview} />
                    );
                } else {
                    const shouldBeDisabled = !!(key > 0 && !itemsArray[key - 1]);

                    const onAddClick = (e: Event): void => {
                        if (shouldBeDisabled) {
                            e.preventDefault();
                            e.stopPropagation();
                        } else if (closeOverlay && typeof closeOverlay === 'function') {
                            closeOverlay();
                        }
                    };

                    card = (
                        <AddProduct
                            external={false}
                            to="/trending?filter=mixable%3Dtrue&mixFlow=active"
                            key={`add-${itemNum}`}
                            isDisabled={shouldBeDisabled}
                            onClick={onAddClick}
                        >
                            <PlusIcon />
                            <SmallHeadline>
                                <PureTranslator tk={`MIX_CHECKOUT_ADD_${itemNum}_ITEM`} />
                            </SmallHeadline>
                        </AddProduct>
                    );
                }

                return (
                    <CSSTransition key={`item-${itemNum}`} timeout={350} classNames="cartItem">
                        {card}
                    </CSSTransition>
                );
            }),
        [closeOverlay, itemsArray, isConfirmation, isOrderReview],
    );

    const handleClickLearnMore = useCallback(() => {
        registerMixLpClick({
            from: 'cartOverlay',
            orderID: orderNumber,
            userID: userId,
            sku: null,
        });
    }, [orderNumber, userId]);

    if (EmptyCardCount === 3) {
        return null;
    }

    return (
        <Container>
            <TransitionGroup component={ContainerBody}>
                <ContainerHeader>
                    <Header>
                        <SmallHeadline>
                            <PureTranslator tk="MIX_CART_OVERLAY_TITLE" />
                        </SmallHeadline>

                        {shouldShowLink && (
                            <HeaderLink to="/mix" onClick={handleClickLearnMore}>
                                <PureTranslator tk="MIXABLE_LEARN_MORE_CTA" />
                            </HeaderLink>
                        )}
                    </Header>

                    <HeaderContent>
                        <HeaderPrice>
                            <Headline>€99</Headline>
                            <HeaderTiny>
                                <PureTranslator tk="MIX_CART_OVERLAY_PRICE_PER_MONTH" />
                            </HeaderTiny>
                        </HeaderPrice>

                        <HeaderList>
                            <TinyParagraph>
                                <PureTranslator tk="MIX_CART_OVERLAY_LIST_PRODUCTS" />
                            </TinyParagraph>
                            <TinyParagraph>
                                <PureTranslator tk="MIX_CART_OVERLAY_LIST_SWAPS" />
                            </TinyParagraph>
                            <TinyParagraph>
                                <PureTranslator tk="MIX_CART_OVERLAY_LIST_DURATION" />
                            </TinyParagraph>
                            <TinyParagraph>
                                <PureTranslator tk="MIX_CART_OVERLAY_LIST_SHIPPING" />
                            </TinyParagraph>
                        </HeaderList>
                    </HeaderContent>
                </ContainerHeader>

                {renderItems}
            </TransitionGroup>
        </Container>
    );
};
